"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Mail,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Building2,
} from "lucide-react";
import type { InvitesOutput, OrganizationsOutput } from "@/interfaces";
import { userRolesTranslations } from "@/interfaces";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";

interface InvitesAccordionProps {
  invites: InvitesOutput[];
  onAcceptInvite: (id: string) => Promise<void>;
  onDeclineInvite: (id: string) => Promise<void>;
}

export function InvitesAccordion({
  invites,
  onAcceptInvite,
  onDeclineInvite,
}: InvitesAccordionProps) {
  // Função para verificar se um convite está próximo de expirar (menos de   {
  // Função para verificar se um convite está próximo de expirar (menos de 3 dias)
  const isExpiringSoon = (expiresAt: string | Date) => {
    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const diffTime = expiryDate.getTime() - now.getTime();
    const diffDays = diffTime / (1000 * 3600 * 24);
    return diffDays < 3 && diffDays > 0;
  };

  return (
    <div className="rounded-xl overflow-hidden shadow-md border border-gray-200">
      <Accordion type="multiple" defaultValue={["invites"]} className="w-full">
        <AccordionItem value="invites" className="border-none">
          <AccordionTrigger className="text-xl font-semibold py-4 bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white hover:from-[#D96E0F] hover:to-[#e58a3d] transition-all px-6 group">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 group-hover:scale-110 transition-transform" />
              <span>Convites de Organizações</span>
              {invites.length > 0 && (
                <span className="ml-2 bg-white text-[#EE7B11] text-sm px-2 py-0.5 rounded-full">
                  {invites.length}
                </span>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="p-0 bg-white">
            <div className="p-6">
              {invites.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl">
                  <div className="bg-[#EE7B11]/10 p-6 rounded-full">
                    <Mail className="h-16 w-16 text-[#EE7B11]" />
                  </div>
                  <div className="text-center max-w-md mt-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      Sem convites pendentes
                    </h3>
                    <p className="text-gray-600">
                      Quando você receber um convite para participar de uma
                      organização, ele aparecerá aqui.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {invites.map((invite, index) => (
                    <motion.div
                      key={invite.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <Card className="overflow-hidden border-none shadow-lg hover:shadow-xl transition-all duration-300">
                        <CardHeader className="bg-gradient-to-r from-[#f8f0e9] to-[#fde7d2] pb-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-xl break-words flex items-center gap-2 text-gray-800">
                                <Building2 className="h-5 w-5 text-[#EE7B11]" />
                                {
                                  (invite.organization as OrganizationsOutput)
                                    ?.name
                                }
                              </CardTitle>
                              <CardDescription className="text-base mt-1">
                                <Badge className="bg-[#EE7B11] hover:bg-[#D96E0F] text-white font-medium">
                                  {userRolesTranslations[invite.role]}
                                </Badge>
                              </CardDescription>
                            </div>
                            {isExpiringSoon(invite.expiresAt) && (
                              <Badge
                                variant="outline"
                                className="border-red-400 text-red-500 font-medium animate-pulse"
                              >
                                Expira em breve
                              </Badge>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="pt-4">
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-gray-700">
                              <Calendar className="h-4 w-4 text-[#EE7B11]" />
                              <span>
                                Convidado em:{" "}
                                <span className="font-medium">
                                  {new Date(
                                    invite.createdAt
                                  ).toLocaleDateString()}
                                </span>
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-700">
                              <Clock className="h-4 w-4 text-[#EE7B11]" />
                              <span>
                                Expira em:{" "}
                                <span className="font-medium">
                                  {new Date(
                                    invite.expiresAt
                                  ).toLocaleDateString()}
                                </span>
                              </span>
                            </div>
                          </div>
                        </CardContent>
                        <CardFooter className="flex justify-end space-x-3 bg-gray-50 py-4 px-6">
                          <Button
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300 transition-colors"
                            onClick={() => onDeclineInvite(invite.id)}
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Recusar
                          </Button>
                          <Button
                            className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] hover:from-[#D96E0F] hover:to-[#e58a3d] text-white"
                            onClick={() => onAcceptInvite(invite.id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Aceitar
                          </Button>
                        </CardFooter>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
